import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
    const { user } = useAuth();
    const [unread, setUnread] = useState(0);
    const [notifUnread, setNotifUnread] = useState(0);
    const [lastMessage, setLastMessage] = useState(null);
    const [lastNotification, setLastNotification] = useState(null);
    const [connected, setConnected] = useState(false);
    const wsRef = useRef(null);
    const pingRef = useRef(null);
    const reconnectRef = useRef(null);

    useEffect(() => {
        if (!user || user === false) {
            // Disconnect on logout
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            setConnected(false);
            setUnread(0);
            setNotifUnread(0);
            return;
        }
        // Initial unread count + notifications count
        api.get("/messages/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
        api.get("/notifications/unread-count").then((r) => setNotifUnread(r.data.count || 0)).catch(() => {});
        connect();
        return () => {
            if (wsRef.current) wsRef.current.close();
            if (pingRef.current) clearInterval(pingRef.current);
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    function connect() {
        const token = localStorage.getItem("edu_token");
        if (!token) return;
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = backend.replace(/^http/, "ws") + "/api/ws?token=" + encodeURIComponent(token);
        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;
            ws.onopen = () => {
                setConnected(true);
                if (pingRef.current) clearInterval(pingRef.current);
                pingRef.current = setInterval(() => {
                    try {
                        ws.send("ping");
                    } catch (_) {}
                }, 30000);
            };
            ws.onmessage = (ev) => {
                try {
                    const data = JSON.parse(ev.data);
                    if (data.type === "unread") {
                        setUnread(data.count || 0);
                    } else if (data.type === "new_message") {
                        setUnread((data.unread != null) ? data.unread : (u) => u + 1);
                        setLastMessage(data.message);
                    } else if (data.type === "exam_result") {
                        // Push notification when child took an exam
                        setNotifUnread((n) => n + 1);
                        setLastNotification(data);
                        const verb =
                            data.grade === "Aprobado"
                                ? "aprobó"
                                : data.grade === "Intermedio"
                                  ? "obtuvo nota intermedia en"
                                  : "desaprobó";
                        const icon = data.grade === "Aprobado" ? "🎉" : data.grade === "Intermedio" ? "📘" : "⚠️";
                        toast(
                            `${icon} ${data.child_name} ${verb} ${data.exam_title}`,
                            {
                                description: `${data.subject} · ${data.score_percent}% (${data.grade})`,
                                duration: 7000,
                            }
                        );
                    } else if (data.type === "grammar_report") {
                        // Reporte automático de corrección gramatical listo para el profesor
                        setNotifUnread((n) => n + 1);
                        setLastNotification(data);
                        toast(`📝 ${data.student_name} — ${data.assignment_title}`, {
                            description: data.error_count > 0
                                ? `${data.error_count} error(es) de gramática detectados`
                                : "Sin errores de gramática relevantes",
                            duration: 6000,
                        });
                    }
                } catch (_) {}
            };
            ws.onclose = () => {
                setConnected(false);
                if (pingRef.current) clearInterval(pingRef.current);
                // try reconnect after 4s if user is still logged in
                if (user && user !== false) {
                    if (reconnectRef.current) clearTimeout(reconnectRef.current);
                    reconnectRef.current = setTimeout(connect, 4000);
                }
            };
            ws.onerror = () => {};
        } catch (e) {
            // ignore
        }
    }

    async function markRead(id) {
        try {
            const { data } = await api.post(`/messages/${id}/read`);
            setUnread(data.unread || 0);
        } catch (_) {}
    }
    async function markAllRead() {
        try {
            await api.post(`/messages/read-all`);
            setUnread(0);
        } catch (_) {}
    }
    async function markNotifRead(id) {
        try {
            const { data } = await api.post(`/notifications/${id}/read`);
            setNotifUnread(data.unread || 0);
        } catch (_) {}
    }
    async function markAllNotifRead() {
        try {
            await api.post(`/notifications/read-all`);
            setNotifUnread(0);
        } catch (_) {}
    }

    return (
        <RealtimeContext.Provider
            value={{
                unread,
                notifUnread,
                lastMessage,
                lastNotification,
                connected,
                markRead,
                markAllRead,
                markNotifRead,
                markAllNotifRead,
            }}
        >
            {children}
        </RealtimeContext.Provider>
    );
}

export function useRealtime() {
    return (
        useContext(RealtimeContext) || {
            unread: 0,
            notifUnread: 0,
            lastMessage: null,
            lastNotification: null,
            connected: false,
            markRead: () => {},
            markAllRead: () => {},
            markNotifRead: () => {},
            markAllNotifRead: () => {},
        }
    );
}
